import type { IxdNoteChecklistLocation } from "@/apis/ixd-editor/types";

import { locationSelectors } from "./finding-location";

type Side = "top" | "right" | "bottom" | "left";

interface GridSlot {
  row: number;
  col: number;
  rowSpan: number;
  colSpan: number;
}

/** 표의 각 칸이 격자에서 차지하는 자리. HTML 표 모델대로 위 행의 rowspan 이 아래 행의 열을 밀어낸다. */
const gridSlots = (table: HTMLTableElement) => {
  const slots = new Map<HTMLTableCellElement, GridSlot>();
  const taken = new Set<string>();
  Array.from(table.rows).forEach((tr, row) => {
    let col = 0;
    for (const cell of Array.from(tr.cells)) {
      while (taken.has(`${row},${col}`)) col++;
      const rowSpan = Math.max(1, cell.rowSpan);
      const colSpan = Math.max(1, cell.colSpan);
      slots.set(cell, { row, col, rowSpan, colSpan });
      for (let r = row; r < row + rowSpan; r++)
        for (let c = col; c < col + colSpan; c++) taken.add(`${r},${c}`);
      col += colSpan;
    }
  });
  return slots;
};

/**
 * 셀 묶음의 바깥쪽 변. 이웃 칸이 같은 묶음이면 그 변은 그리지 않아, 붙은 셀들이 테두리 하나로 묶인다.
 * 표가 다르면 다른 묶음이다. 태깅 페이지 당기/전기 테두리(components/workspace)와 같은 규칙.
 */
export const outerEdges = (
  cells: HTMLTableCellElement[],
): Map<HTMLTableCellElement, Side[]> => {
  const edges = new Map<HTMLTableCellElement, Side[]>();
  const byTable = new Map<HTMLTableElement, HTMLTableCellElement[]>();
  for (const cell of cells) {
    const table = cell.closest("table");
    if (table) byTable.set(table, [...(byTable.get(table) ?? []), cell]);
  }
  for (const [table, group] of byTable) {
    const slots = gridSlots(table);
    const inGroup = new Set<string>();
    for (const cell of group) {
      const s = slots.get(cell);
      if (!s) continue;
      for (let r = s.row; r < s.row + s.rowSpan; r++)
        for (let c = s.col; c < s.col + s.colSpan; c++)
          inGroup.add(`${r},${c}`);
    }
    for (const cell of group) {
      const s = slots.get(cell);
      if (!s) continue;
      const open = (positions: [number, number][]) =>
        positions.some(([r, c]) => !inGroup.has(`${r},${c}`));
      const cols = Array.from({ length: s.colSpan }, (_, i) => s.col + i);
      const rows = Array.from({ length: s.rowSpan }, (_, i) => s.row + i);
      const sides: Side[] = [];
      if (open(cols.map((c) => [s.row - 1, c]))) sides.push("top");
      if (open(rows.map((r) => [r, s.col + s.colSpan]))) sides.push("right");
      if (open(cols.map((c) => [s.row + s.rowSpan, c]))) sides.push("bottom");
      if (open(rows.map((r) => [r, s.col - 1]))) sides.push("left");
      edges.set(cell, sides);
    }
  }
  return edges;
};

// 셀 테두리는 inset 그림자로 그린다 — border-collapse 표에서 border 를 두껍게 하면 레이아웃이 밀린다.
const INSET: Record<Side, string> = {
  top: "inset 0 2px 0 0 var(--status-negative)",
  right: "inset -2px 0 0 0 var(--status-negative)",
  bottom: "inset 0 -2px 0 0 var(--status-negative)",
  left: "inset 2px 0 0 0 var(--status-negative)",
};

// 상시 표시(점선, offset 4px)와 같은 자리에 실선으로 겹친다.
const WHOLE_OUTLINE =
  "outline:2px solid var(--status-negative)!important;outline-offset:4px";

/**
 * 선택한 지적의 테두리 CSS. 셀 위치는 붙은 셀끼리 바깥 테두리 하나로, 표·목차 위치는 그 요소 외곽선으로.
 * 셀 묶음은 DOM 격자를 읽어야 해서 선택 시점에 한 번 계산한다.
 */
export const selectedOutlineCss = (
  container: ParentNode,
  scopeId: string,
  locations: IxdNoteChecklistLocation[],
): string => {
  const rules: string[] = [];
  const cells: [string, HTMLTableCellElement][] = [];
  for (const location of locations) {
    for (const selector of locationSelectors([location])) {
      if (location.cellIds.length === 0) {
        rules.push(`#${scopeId} ${selector}{${WHOLE_OUTLINE}}`);
        continue;
      }
      const cell = container.querySelector(selector);
      if (cell instanceof HTMLTableCellElement) cells.push([selector, cell]);
    }
  }
  const edges = outerEdges(cells.map(([, cell]) => cell));
  for (const [selector, cell] of cells) {
    const sides = edges.get(cell) ?? [];
    if (sides.length === 0) continue;
    const shadow = sides.map((side) => INSET[side]).join(",");
    rules.push(`#${scopeId} ${selector}{box-shadow:${shadow}!important}`);
  }
  return rules.join("");
};
